import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../hooks/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";

export default function LoginForm() {
  // --- Local State for the Form ---
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // --- Hooks for Authentication and Navigation ---
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // --- Effect for Role-Based Redirection ---
  useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on the user's role.
      if (user.role === "hr") {
        navigate("/"); // HR dashboard
      } else if (user.role === "employee") {
        navigate("/"); // Redirect employees to the task page
      } else {
        // Fallback for any other roles or just a default dashboard
        navigate("/");
      }
    }
  }, [isAuthenticated, user, navigate]);

  // --- Form Submission Handler ---


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
  
    try {
      // Call the login function from the AuthContext
      await login(email, password);
  
      // ✅ Show success SweetAlert
      Swal.fire({
        icon: "success",
        title: "Login successful!",
        showConfirmButton: false,
        timer: 2000,
      });
  
      setIsLoading(false);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Login failed. Please check your credentials.";
  
      setError(message);
      setIsLoading(false);
  
      // ❌ Show error SweetAlert
      Swal.fire({
        icon: "error",
        title: "Login failed",
        text: message,
        confirmButtonText: "OK",
      });
    }
  };
  
  

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-[#2C365E] -skew-y-6 transform origin-top-left z-0"></div>

      {/* Login Card */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 rounded shadow-md max-w-md w-full p-8"
        >
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-1">
            LOGIN
          </h2>
          <p className="text-sm text-center text-gray-500 mb-6">
            Please Enter Your Details
          </p>

          <div className="space-y-4">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs text-gray-600 mb-1 uppercase"
              >
                Email Address {/* Changed from "User Name" to "Email" */}
              </label>
              <input
                type="email"
                id="email"
                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className="block text-xs text-gray-600 mb-1 uppercase"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="w-full p-3 border border-gray-300 rounded pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 text-gray-400 hover:text-gray-600 flex items-center"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <p className="text-red-500 text-sm text-center font-semibold">
                {error}
              </p>
            )}

            {/* Forgot Password Button */}
            <div className="text-sm text-right">
              <Link
                to="/forgot-password"
                className="text-[#2C4E86] hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              className="w-full bg-[#001E6C] text-white py-3 rounded hover:bg-[#00124a] transition duration-200 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
